import React, { useEffect, useState, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { thumbUrl } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaintBrushIcon as PaintBrush, CheckCircleIcon as CheckCircle, HourglassIcon as Hourglass, XCircleIcon as XCircle, ArrowRightIcon as ArrowRight, StorefrontIcon as Storefront } from "@phosphor-icons/react";

/**
 * Public buyer status page for a kennel-storefront custom order.
 * Mirror of `ArmstrongCustomOrder.jsx` but scoped to a tenant store
 * (`/store/:slug/custom/:orderId`). The slug is part of the URL so
 * we can render the seller's branding in the header.
 *
 * The order_id (UUID4) is the access token — no buyer auth needed.
 */
export default function StoreCustomOrder() {
  const { slug, orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [directAck, setDirectAck] = useState(false);

  const justPaid = params.get("status") === "paid";

  const load = useCallback(() => {
    api.get(`/store/${slug}/custom/order/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); });
  }, [slug, orderId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (justPaid) toast.success("Payment received — the seller will get on your order right away.");
  }, [justPaid]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/store/${slug}/custom/order/${orderId}/pay`, {
        origin_url: webOrigin(),
        direct_payment_ack: directAck,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout.");
      setPaying(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] p-6 text-center">
        <XCircle size={42} weight="duotone" className="text-[#9A9A9A] mb-4" />
        <h1 className="font-serif text-2xl mb-2">Order not found</h1>
        <p className="text-[#6B6B6B] mb-6">This custom order link is invalid or has expired.</p>
        <Link to={`/store/${slug}`} className="text-[#0A6BFF] underline">Back to the store →</Link>
      </div>
    );
  }

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">Loading…</div>;
  }

  const store = order.store || {};
  const accent = store.accent_color || "#0A6BFF";
  const status = order.status;
  const canPay = status === "quoted" || status === "mockup_sent";
  const paid = status === "paid" || status === "fulfilled";
  const mockupSrc = order.mockup_url ? (thumbUrl(order.mockup_url) || order.mockup_url) : null;
  const total = (order.quoted_price_usd || 0) * (order.quantity || 1);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>{`Your custom order — ${store.name || "Kennel"}`}</title>
      </Helmet>

      <header
        className="relative text-white py-12 px-6"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, #111 120%)` }}
        data-testid="store-custom-order-header"
      >
        <div className="max-w-3xl mx-auto">
          <Link to={`/store/${slug}`} className="text-[11px] text-white/70 hover:text-white uppercase tracking-[0.2em] inline-flex items-center gap-1">
            <Storefront size={11} weight="fill" /> {store.name || "Back to store"}
          </Link>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/85 mt-4 mb-2 flex items-center gap-1.5">
            <PaintBrush size={12} weight="fill" /> Custom order
          </p>
          <h1 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05]">
            {paid ? "All set — they got it" :
             status === "declined" ? "Update on your request" :
             "Your custom order"}
          </h1>
          <p className="mt-3 text-white/70 text-sm font-mono">Order #{order.id.slice(0, 8).toUpperCase()}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-6">
        <StatusPill status={status} />

        <div className="bg-white rounded-xl border p-6" data-testid="store-custom-summary">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B]">Your request</p>
          <p className="text-[15px] text-[#3a3a3a] leading-relaxed mt-2 whitespace-pre-wrap">{order.description}</p>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            {order.product_reference && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Base item</p>
                <p className="mt-0.5">{order.product_reference}</p>
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Quantity</p>
              <p className="font-mono mt-0.5">{order.quantity}</p>
            </div>
            {order.quoted_price_usd && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Per-unit price</p>
                <p className="font-mono mt-0.5">${order.quoted_price_usd.toFixed(2)}</p>
              </div>
            )}
          </div>
        </div>

        {mockupSrc && (
          <div className="bg-white rounded-xl border p-6" data-testid="store-custom-mockup">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B] mb-3">
              The mockup {store.name} sent for you
            </p>
            <img src={mockupSrc} alt="Custom mockup" className="w-full rounded-lg max-h-[480px] object-contain bg-[#FAFAFA]" />
            {order.seller_notes && (
              <p className="text-[13px] text-[#3a3a3a] mt-4 leading-relaxed whitespace-pre-wrap">{order.seller_notes}</p>
            )}
          </div>
        )}

        {canPay && (
          <div className="rounded-xl border-2 p-6" style={{ borderColor: accent, background: `${accent}10` }} data-testid="store-custom-pay-card">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: accent }}>Approve and pay</p>
            <p className="font-serif text-3xl mt-1">${total.toFixed(2)} <span className="text-[#6B6B6B] text-base">total</span></p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed">
              {order.quantity}× at ${(order.quoted_price_usd || 0).toFixed(2)} each.
              Payment is processed by Stripe. {store.name} is the merchant of record;
              Armstrong K9 Registry takes a {(order.platform_fee_pct * 100).toFixed(0)}% platform fee.
            </p>
            {order.payment_model === "direct" && (
              <div className="mt-4 rounded-lg border-2 border-[#E5A33B]/60 bg-[#FFF8EC] p-3 text-[12px] leading-snug text-[#3B2A07] text-left" data-testid="custom-order-direct-block">
                <p className="font-bold uppercase tracking-[0.18em] text-[9.5px] text-[#8A5A00] mb-1.5">
                  International seller — direct payment
                </p>
                <p className="mb-2">
                  <strong>{store.name} is located outside the United States.</strong>{" "}
                  Your payment goes directly to their own Stripe account — they are
                  the merchant of record. Armstrong K9 Registry never holds these
                  funds, and <strong>no Registry escrow or dispute protection
                  applies</strong>. Refunds and disputes are handled solely by the seller.
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={directAck}
                    onChange={(e) => setDirectAck(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#8A5A00]"
                    data-testid="custom-order-direct-checkbox"
                  />
                  <span>
                    I understand my payment goes directly to {store.name} with no
                    Registry escrow or dispute protection.
                  </span>
                </label>
              </div>
            )}
            <Button
              onClick={pay}
              disabled={paying || (order.payment_model === "direct" && !directAck)}
              className="mt-5 rounded-full text-white h-12 px-6 font-semibold"
              style={{ background: accent }}
              data-testid="store-custom-pay-btn"
            >
              {paying ? "Opening checkout…" : `Pay $${total.toFixed(2)} →`}
            </Button>
          </div>
        )}

        {status === "declined" && (
          <div className="bg-red-50 border-red-300 border rounded-xl p-6" data-testid="store-custom-declined">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-red-700">{store.name} wasn&rsquo;t able to take this on</p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed whitespace-pre-wrap">
              {order.decline_reason || "Unfortunately we can't fulfill this request right now."}
            </p>
            <Link to={`/store/${slug}`} className="inline-block mt-4 underline text-sm" style={{ color: accent }}>
              Browse the store →
            </Link>
          </div>
        )}

        {paid && (
          <div className="bg-emerald-50 border-emerald-300 border rounded-xl p-6" data-testid="store-custom-paid">
            <CheckCircle size={32} weight="duotone" className="text-emerald-700 mb-2" />
            <p className="font-serif text-2xl">Thanks — {store.name} is on it.</p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed">
              {status === "fulfilled"
                ? "Your order has been fulfilled and shipped. Watch your email for tracking."
                : "Your payment is confirmed. The seller will be in touch directly with shipping updates."}
            </p>
          </div>
        )}

        {status === "requested" && !mockupSrc && (
          <div className="bg-amber-50 border-amber-300 border rounded-xl p-6" data-testid="store-custom-waiting">
            <Hourglass size={28} weight="duotone" className="text-amber-700 mb-2" />
            <p className="font-serif text-xl">{store.name} has got your request</p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed">
              They&rsquo;ll review and send you a mockup soon. You&rsquo;ll get an email
              and this page will update automatically. Save the link — it&rsquo;s your home base
              for this order.
            </p>
          </div>
        )}

        <div className="pt-4 text-center">
          <Link to={`/store/${slug}`} className="inline-flex items-center gap-1.5 text-[12px] text-[#6B6B6B] hover:text-[#0E0E0E]">
            Back to {store.name || "store"} <ArrowRight size={11} />
          </Link>
        </div>
      </main>
    </div>
  );
}

const STATUS_META = {
  requested: { label: "Awaiting mockup", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  mockup_sent: { label: "Mockup ready — review and pay", cls: "bg-blue-50 text-blue-900 border-blue-300" },
  quoted: { label: "Mockup ready — review and pay", cls: "bg-blue-50 text-blue-900 border-blue-300" },
  paid: { label: "Paid — in production", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  fulfilled: { label: "Fulfilled", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  declined: { label: "Unable to fulfill", cls: "bg-red-50 text-red-700 border-red-300" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "bg-gray-100 text-gray-700 border-gray-300" };
  return (
    <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full px-3 py-1 border ${meta.cls}`} data-testid={`store-status-${status}`}>
      {meta.label}
    </div>
  );
}
